import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, MapPin, Calendar, ShieldAlert, CalendarOff } from "lucide-react";
import { toast } from "sonner";
import { getCar, createBooking, getAvailability } from "../lib/api";
import { imgUrl } from "../lib/img";
import { CONTRACT_TERMS } from "../lib/contract";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

const LOCATIONS = ["Los Angeles Downtown HQ", "LAX International Airport", "Beverly Hills Boutique", "Santa Monica Pier"];
const STEPS = ["Dates & Location", "Your Details", "Rental Agreement", "Confirmed"];

const fieldCls = "bg-transparent border-0 border-b border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-[#D4AF37] px-0 text-white placeholder:text-white/30";

export default function Booking() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [unavailable, setUnavailable] = useState([]);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const [form, setForm] = useState({
    pickup_location: LOCATIONS[0],
    dropoff_location: LOCATIONS[0],
    pickup_date: "",
    dropoff_date: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    agreed_terms: false,
  });

  useEffect(() => {
    getCar(slug).then(setCar).catch(() => navigate("/fleet"));
    getAvailability(slug).then(setUnavailable).catch(() => setUnavailable([]));
  }, [slug, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const overlapsUnavailable = (start, end) =>
    unavailable.find((r) => start < r.end && r.start < end);

  const days = (() => {
    if (!form.pickup_date || !form.dropoff_date) return 0;
    const d = (new Date(form.dropoff_date) - new Date(form.pickup_date)) / 86400000;
    return d > 0 ? Math.ceil(d) : 0;
  })();
  const total = car ? days * car.price_per_day : 0;

  const validateStep = () => {
    if (step === 0) {
      if (!form.pickup_date || !form.dropoff_date) return "Please choose both pickup and drop-off dates.";
      if (new Date(form.dropoff_date) <= new Date(form.pickup_date)) return "Drop-off must be after pickup.";
      const clash = overlapsUnavailable(form.pickup_date, form.dropoff_date);
      if (clash) return `This car is unavailable ${clash.start} to ${clash.end}. Please pick other dates.`;
      return null;
    }
    if (step === 1) {
      if (!form.first_name.trim() || !form.last_name.trim()) return "Please enter your full name.";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return "Please enter a valid email.";
      if (form.phone.trim().length < 7) return "Please enter a valid phone number.";
      return null;
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    if (!form.agreed_terms) { toast.error("You must accept the rental agreement to continue."); return; }
    setSubmitting(true);
    try {
      const res = await createBooking({ car_slug: slug, ...form });
      setConfirmation(res);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!car) return <div className="pt-40 text-center text-white/40">Loading…</div>;

  return (
    <div data-testid="booking-page" className="pt-28 pb-24 min-h-screen">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        {step < 3 && (
          <button
            data-testid="booking-back-link"
            onClick={() => navigate(`/fleet/${slug}`)}
            className="flex items-center gap-2 text-white/50 hover:text-white text-sm mb-10"
          >
            <ArrowLeft size={16} /> Back to vehicle
          </button>
        )}

        {/* Stepper */}
        <div className="flex items-center justify-between mb-14 max-w-2xl">
          {STEPS.map((label, i) => (
            <div key={label} className="flex-1 flex items-center">
              <div className="flex flex-col items-center">
                <div
                  data-testid={`step-indicator-${i}`}
                  className={`w-9 h-9 flex items-center justify-center border text-sm ${
                    i < step ? "bg-[#D4AF37] border-[#D4AF37] text-black"
                    : i === step ? "bg-white border-white text-black"
                    : "border-white/20 text-white/40"
                  }`}
                >
                  {i < step ? <Check size={16} /> : i + 1}
                </div>
                <span className={`text-[10px] uppercase tracking-wider mt-2 hidden sm:block ${i === step ? "text-white" : "text-white/40"}`}>
                  {label}
                </span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? "bg-[#D4AF37]" : "bg-white/15"}`} />}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-10">
          {/* Main panel */}
          <div className="lg:col-span-2">
            <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Step {Math.min(step + 1, 4)} of 4</p>
            <h1 className="font-serif text-4xl tracking-tight mb-8">{STEPS[step]}</h1>

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Step 0 */}
                {step === 0 && (
                  <div className="space-y-8">
                    {unavailable.length > 0 && (
                      <div data-testid="unavailable-note" className="border border-white/15 bg-white/5 p-4 text-sm">
                        <p className="text-white/70 flex items-center gap-2 mb-2"><CalendarOff size={14} className="text-[#D4AF37]" /> Already booked / unavailable — please avoid these dates:</p>
                        <ul className="text-white/50 space-y-1">
                          {unavailable.map((r, i) => (
                            <li key={`${r.start}-${i}`}>· {r.start} → {r.end}{r.type === "block" && r.reason ? ` (${r.reason})` : ""}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-8">
                      <div>
                        <Label className="text-white/60 text-xs uppercase tracking-wider flex items-center gap-2 mb-3"><MapPin size={13} /> Pickup Location</Label>
                        <select
                          data-testid="pickup-location-select"
                          value={form.pickup_location}
                          onChange={(e) => set("pickup_location", e.target.value)}
                          className="w-full bg-transparent border-b border-white/20 py-2 focus:outline-none focus:border-[#D4AF37] text-white"
                        >
                          {LOCATIONS.map((l) => <option key={l} value={l} className="bg-[#0F0F0F]">{l}</option>)}
                        </select>
                      </div>
                      <div>
                        <Label className="text-white/60 text-xs uppercase tracking-wider flex items-center gap-2 mb-3"><MapPin size={13} /> Drop-off Location</Label>
                        <select
                          data-testid="dropoff-location-select"
                          value={form.dropoff_location}
                          onChange={(e) => set("dropoff_location", e.target.value)}
                          className="w-full bg-transparent border-b border-white/20 py-2 focus:outline-none focus:border-[#D4AF37] text-white"
                        >
                          {LOCATIONS.map((l) => <option key={l} value={l} className="bg-[#0F0F0F]">{l}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-8">
                      <div>
                        <Label className="text-white/60 text-xs uppercase tracking-wider flex items-center gap-2 mb-3"><Calendar size={13} /> Pickup Date</Label>
                        <Input
                          type="date" data-testid="pickup-date-input"
                          value={form.pickup_date}
                          min={new Date().toISOString().split("T")[0]}
                          onChange={(e) => set("pickup_date", e.target.value)}
                          className={`${fieldCls} [color-scheme:dark]`}
                        />
                      </div>
                      <div>
                        <Label className="text-white/60 text-xs uppercase tracking-wider flex items-center gap-2 mb-3"><Calendar size={13} /> Drop-off Date</Label>
                        <Input
                          type="date" data-testid="dropoff-date-input"
                          value={form.dropoff_date}
                          min={form.pickup_date || new Date().toISOString().split("T")[0]}
                          onChange={(e) => set("dropoff_date", e.target.value)}
                          className={`${fieldCls} [color-scheme:dark]`}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 1 */}
                {step === 1 && (
                  <div className="space-y-8">
                    <div className="grid sm:grid-cols-2 gap-8">
                      <div>
                        <Label className="text-white/60 text-xs uppercase tracking-wider mb-3 block">First Name</Label>
                        <Input data-testid="first-name-input" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="John" className={fieldCls} />
                      </div>
                      <div>
                        <Label className="text-white/60 text-xs uppercase tracking-wider mb-3 block">Last Name</Label>
                        <Input data-testid="last-name-input" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Carter" className={fieldCls} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider mb-3 block">Email</Label>
                      <Input type="email" data-testid="email-input" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="john@example.com" className={fieldCls} />
                    </div>
                    <div>
                      <Label className="text-white/60 text-xs uppercase tracking-wider mb-3 block">Phone Number</Label>
                      <Input data-testid="phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 555 019 9999" className={fieldCls} />
                    </div>
                  </div>
                )}

                {/* Step 2 */}
                {step === 2 && (
                  <div>
                    <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 mb-5 flex gap-3">
                      <ShieldAlert size={18} className="text-[#D4AF37] shrink-0 mt-0.5" />
                      <p data-testid="insurance-notice" className="text-sm text-white/80 leading-relaxed">
                        <span className="text-white font-medium">Insurance is your responsibility.</span> You
                        must provide your own valid third-party / liability insurance before pickup. Without it,
                        full liability falls on you.
                      </p>
                    </div>
                    <div
                      data-testid="contract-terms-box"
                      className="h-72 overflow-y-auto border border-white/10 bg-[#0A0A0A] p-6 text-white/60 text-sm leading-relaxed whitespace-pre-line"
                    >
                      {CONTRACT_TERMS}
                    </div>
                    <label className="flex items-start gap-3 mt-6 cursor-pointer">
                      <Checkbox
                        data-testid="agree-terms-checkbox"
                        checked={form.agreed_terms}
                        onCheckedChange={(v) => set("agreed_terms", !!v)}
                        className="mt-0.5 rounded-none border-white/40 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                      />
                      <span className="text-sm text-white/70">
                        I have read and agree to the A+Auto Fleet Vehicle Rental Agreement and confirm this constitutes a legally binding signature.
                      </span>
                    </label>
                  </div>
                )}

                {/* Step 3 - confirmation */}
                {step === 3 && confirmation && (
                  <div data-testid="confirmation-panel" className="text-center py-6">
                    <CheckCircle2 size={56} className="text-[#D4AF37] mx-auto" strokeWidth={1.3} />
                    <h2 className="font-serif text-4xl mt-6">You're all set.</h2>
                    <p className="text-white/50 mt-3">Your reservation is confirmed. A copy has been sent to {confirmation.email}.</p>
                    <div className="border border-white/10 bg-[#0A0A0A] mt-8 p-8 text-left max-w-md mx-auto">
                      <p className="overline mb-2">Booking Reference</p>
                      <p data-testid="booking-reference" className="font-serif text-3xl text-[#D4AF37] tracking-wider">{confirmation.reference}</p>
                      <div className="mt-6 space-y-3 text-sm border-t border-white/10 pt-6">
                        <Row l="Vehicle" v={confirmation.car_name} />
                        <Row l="Pickup" v={`${confirmation.pickup_location}`} />
                        <Row l="Dates" v={`${confirmation.pickup_date} → ${confirmation.dropoff_date}`} />
                        <Row l="Duration" v={`${confirmation.days} day(s)`} />
                        <Row l="Total (pay at pickup)" v={`$${confirmation.total_price}`} accent />
                      </div>
                    </div>
                    <div className="flex gap-4 justify-center mt-8">
                      <Link to="/fleet" data-testid="browse-more-btn" className="border border-white/20 px-6 py-3 hover:bg-white/5 text-sm">Browse more cars</Link>
                      <Link to="/" data-testid="back-home-btn" className="bg-white text-black px-6 py-3 hover:bg-[#D4AF37] transition-colors text-sm">Back home</Link>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Nav buttons */}
            {step < 3 && (
              <div className="flex items-center justify-between mt-12 pt-8 border-t border-white/10">
                <button
                  data-testid="prev-step-btn"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  className="text-white/50 hover:text-white text-sm disabled:opacity-30"
                >
                  ← Previous
                </button>
                {step < 2 ? (
                  <button
                    data-testid="next-step-btn"
                    onClick={next}
                    className="group bg-white text-black px-8 py-3 flex items-center gap-2 hover:bg-[#D4AF37] transition-colors"
                  >
                    Continue <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                ) : (
                  <button
                    data-testid="confirm-booking-btn"
                    onClick={submit}
                    disabled={!form.agreed_terms || submitting}
                    className="bg-white text-black px-8 py-3 hover:bg-[#D4AF37] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Confirming…" : "Confirm Booking"}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Summary sidebar */}
          {step < 3 && (
            <div className="lg:col-span-1">
              <div className="border border-white/10 bg-[#0F0F0F] sticky top-28">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={imgUrl(car.image)} alt={car.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <p className="text-white/40 text-xs uppercase tracking-wider">{car.brand}</p>
                  <h3 className="font-serif text-2xl mt-1">{car.name}</h3>
                  <div className="mt-6 space-y-3 text-sm border-t border-white/10 pt-6">
                    <Row l="Weekly rate" v={`$${car.price_per_week}`} />
                    <Row l="Daily rate" v={`$${car.price_per_day}`} />
                    <Row l="Duration" v={days > 0 ? `${days} day(s)` : "—"} />
                    <div className="flex justify-between border-t border-white/10 pt-4 mt-2">
                      <span className="text-white/60">Estimated total</span>
                      <span data-testid="summary-total" className="text-[#D4AF37] text-xl font-light">${total || 0}</span>
                    </div>
                  </div>
                  <p className="text-white/30 text-xs mt-4">Unlimited miles · Payable at pickup · No deposit</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Row = ({ l, v, accent }) => (
  <div className="flex justify-between gap-4">
    <span className="text-white/50">{l}</span>
    <span className={accent ? "text-[#D4AF37] font-medium" : "text-white text-right"}>{v}</span>
  </div>
);
